import { useState, useEffect, useCallback } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useTranslation } from "react-i18next";

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f5f4f0",
    fontFamily: "'Georgia', serif",
    padding: "1rem",
  },
  container: {
    maxWidth: "780px",
    margin: "0 auto",
    background: "#ffffff",
    border: "1px solid #e0ddd6",
    borderRadius: "2px",
    padding: "clamp(1.25rem, 5vw, 3rem)",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "1.5rem",
    marginBottom: "2rem",
    paddingBottom: "1.5rem",
    borderBottom: "2px solid #1a1a1a",
  },
  headerLeft: {
    flex: "1 1 220px",
    minWidth: 0,
  },
  headerRight: {
    flex: "0 1 auto",
    textAlign: "right",
    minWidth: "min(180px, 100%)",
  },
  invoiceTitle: {
    fontSize: "clamp(32px, 8vw, 48px)",
    fontWeight: "400",
    letterSpacing: "-1px",
    color: "#1a1a1a",
    lineHeight: 1,
    fontFamily: "'Georgia', serif",
  },
  offerTitleInput: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid #e0ddd6",
    background: "transparent",
    fontSize: "18px",
    fontStyle: "italic",
    color: "#555",
    padding: "6px 0",
    outline: "none",
    fontFamily: "'Georgia', serif",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
    marginTop: "8px",
    marginBottom: "4px",
  },
  label: {
    fontSize: "10px",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#999",
    marginBottom: "4px",
    fontFamily: "'Georgia', serif",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  input: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid #ddd",
    background: "transparent",
    fontSize: "14px",
    color: "#1a1a1a",
    padding: "4px 0",
    outline: "none",
    fontFamily: "'Georgia', serif",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  },
  fieldGroup: {
    marginBottom: "1rem",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "1rem 1.5rem",
  },
  sectionLabel: {
    fontSize: "10px",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#1a1a1a",
    marginBottom: "1rem",
    paddingBottom: "6px",
    borderBottom: "1px solid #1a1a1a",
    fontFamily: "'Georgia', serif",
  },
  section: {
    marginBottom: "2rem",
  },
  tableWrapper: {
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    marginLeft: "-clamp(1.25rem, 5vw, 3rem)",
    marginRight: "-clamp(1.25rem, 5vw, 3rem)",
    paddingLeft: "clamp(1.25rem, 5vw, 3rem)",
    paddingRight: "clamp(1.25rem, 5vw, 3rem)",
  },
  table: {
    width: "100%",
    minWidth: "400px",
    borderCollapse: "collapse",
    fontSize: "13px",
    fontFamily: "'Georgia', serif",
    border: "1px solid #e8e5e0",
    borderRadius: "2px",
  },
  th: {
    fontSize: "10px",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#666",
    padding: "11px 10px",
    background: "#f5f4f0",
    borderBottom: "2px solid #dedad3",
    textAlign: "left",
    fontWeight: "600",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "11px 10px",
    borderBottom: "1px solid #ede9e3",
    verticalAlign: "middle",
  },
  tableInput: {
    width: "100%",
    border: "none",
    background: "transparent",
    fontSize: "13px",
    color: "#1a1a1a",
    outline: "none",
    fontFamily: "'Georgia', serif",
    padding: "0",
  },
  amountCell: {
    textAlign: "right",
    color: "#1a1a1a",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    fontWeight: "600",
    background: "#faf9f7",
    fontSize: "13px",
  },
  addRowBtn: {
    background: "none",
    border: "1px dashed #d5d1cb",
    borderRadius: "2px",
    cursor: "pointer",
    fontSize: "12px",
    color: "#999",
    padding: "9px 0",
    letterSpacing: "0.06em",
    fontFamily: "'Georgia', serif",
    marginTop: "8px",
    width: "100%",
    textAlign: "center",
    display: "block",
  },
  deleteRowBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#d0cbc4",
    fontSize: "18px",
    lineHeight: 1,
    padding: "0 4px",
    fontFamily: "'Georgia', serif",
  },
  totalsNotesRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "1.5rem",
    marginBottom: "2rem",
  },
  notesBox: {
    flex: "1 1 200px",
    minWidth: 0,
  },
  totalsBlock: {
    flex: "0 1 260px",
    width: "100%",
    fontFamily: "'Georgia', serif",
    fontSize: "13px",
  },
  totalsRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "7px 0",
    borderBottom: "1px solid #f0ede8",
    color: "#555",
  },
  totalsTotal: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px 0 0",
    borderTop: "2px solid #1a1a1a",
    marginTop: "4px",
    fontWeight: "700",
    fontSize: "16px",
    color: "#1a1a1a",
  },
  taxInput: {
    width: "36px",
    border: "none",
    borderBottom: "1px dashed #ccc",
    background: "transparent",
    fontSize: "13px",
    color: "#555",
    textAlign: "center",
    outline: "none",
    fontFamily: "'Georgia', serif",
  },
  notes: {
    width: "100%",
    border: "1px solid #e0ddd6",
    borderRadius: "2px",
    background: "#fafaf8",
    fontSize: "13px",
    color: "#1a1a1a",
    padding: "10px",
    outline: "none",
    fontFamily: "'Georgia', serif",
    resize: "vertical",
    minHeight: "80px",
    lineHeight: "1.6",
    boxSizing: "border-box",
  },
  footerRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1rem",
    paddingTop: "1.5rem",
    borderTop: "1px solid #e0ddd6",
  },
  printBtn: {
    background: "#1a1a1a",
    color: "#fff",
    border: "none",
    padding: "12px 28px",
    fontSize: "12px",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    cursor: "pointer",
    fontFamily: "'Georgia', serif",
    whiteSpace: "nowrap",
  },
  toggleBtn: {
    background: "none",
    border: "1px solid #ddd",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "11px",
    color: "#aaa",
    padding: "3px 10px",
    fontFamily: "'Georgia', serif",
    letterSpacing: "0.04em",
  },
  toggleBtnActive: {
    background: "#1a1a1a",
    border: "1px solid #1a1a1a",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "11px",
    color: "#fff",
    padding: "3px 10px",
    fontFamily: "'Georgia', serif",
    letterSpacing: "0.04em",
  },
};

function Field({ label, value, onChange, placeholder, style, type }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={styles.fieldGroup}>
      {label && <div style={styles.label}>{label}</div>}
      <input
        type={type || "text"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          ...styles.input,
          ...(focused ? { borderBottomColor: "#1a1a1a" } : {}),
          ...(style || {}),
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    </div>
  );
}

function newRow() {
  return { id: Date.now() + Math.random(), description: "", qty: "", rate: "" };
}

export default function CreateOffer() {
  const { t } = useTranslation();
  const due = new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0];

  const [offerTitle, setOfferTitle] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [senderPhone, setSenderPhone] = useState("");
  const [validUntil, setValidUntil] = useState(due);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [clientCity, setClientCity] = useState("");
  const [rows, setRows] = useState([newRow(), newRow()]);
  const [taxRate, setTaxRate] = useState("18");
  const [includeTax, setIncludeTax] = useState(true);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return;
      const user = JSON.parse(raw);
      if (user.name) setSenderName(user.name);
      if (user.email) setSenderEmail(user.email);
      if (user.phoneNumber) setSenderPhone(user.phoneNumber);
    } catch (err) {
      console.error("Could not read user from localStorage:", err);
    }
  }, []);

  const updateRow = useCallback((id, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  }, []);

  const deleteRow = useCallback((id) => {
    setRows((prev) =>
      prev.length > 1 ? prev.filter((r) => r.id !== id) : prev,
    );
  }, []);

  const addRow = () => setRows((prev) => [...prev, newRow()]);

  const getAmount = (row) =>
    (parseFloat(row.qty) || 0) * (parseFloat(row.rate) || 0);
  const subtotal = rows.reduce((s, r) => s + getAmount(r), 0);
  const tax = (subtotal * (parseFloat(taxRate) || 0)) / 100;
  const total = includeTax ? subtotal + tax : subtotal;
  const fmt = (n) =>
    n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const handlePrint = async () => {
    const el = document.getElementById("invoice-printable");
    if (!el) return;

    const liveFields = Array.from(el.querySelectorAll("input, textarea"));
    const fieldValues = liveFields.map((f) => f.value);

    const clone = el.cloneNode(true);
    clone.style.position = "fixed";
    clone.style.top = "-99999px";
    clone.style.left = "0px";
    clone.style.width = "780px";
    clone.style.maxWidth = "780px";
    clone.style.direction = "ltr";

    clone.querySelectorAll(".no-print").forEach((n) => n.remove());

    const tw = clone.querySelector("#invoice-table-wrapper");
    if (tw) {
      tw.style.marginLeft = "0";
      tw.style.marginRight = "0";
      tw.style.paddingLeft = "0";
      tw.style.paddingRight = "0";
    }

    const clonedFields = Array.from(clone.querySelectorAll("input, textarea"));
    clonedFields.forEach((field, i) => {
      const value = fieldValues[i] || "";
      const div = document.createElement("div");
      div.setAttribute("style", field.getAttribute("style") || "");
      div.style.height = "auto";
      div.style.overflow = "visible";
      div.style.lineHeight = "1.5";
      div.style.direction = "ltr";
      div.style.whiteSpace = "pre-wrap";
      div.style.wordBreak = "break-word";
      div.textContent = value;
      field.replaceWith(div);
    });

    document.body.appendChild(clone);

    try {
      await document.fonts.ready;

      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: 780,
        windowWidth: 900,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const margin = 10;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentWidth = pageWidth - margin * 2;
      const contentHeight = (canvas.height * contentWidth) / canvas.width;
      const pageContentHeight = pageHeight - margin * 2;

      let yOffset = 0;
      let remaining = contentHeight;
      let firstPage = true;
      while (remaining > 0) {
        if (!firstPage) pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          margin,
          margin - yOffset,
          contentWidth,
          contentHeight,
        );
        yOffset += pageContentHeight;
        remaining -= pageContentHeight;
        firstPage = false;
      }

      const safeName =
        offerTitle.trim().replace(/\s+/g, "-").toLowerCase() || "offer";
      pdf.save(`${safeName}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      if (document.body.contains(clone)) document.body.removeChild(clone);
    }
  };

  return (
    <div style={styles.page}>
      <div id="invoice-printable" style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.invoiceTitle}>{t("offer")}</div>
            <input
              type="text"
              value={offerTitle}
              onChange={(e) => setOfferTitle(e.target.value)}
              placeholder={t("offerTitlePlaceholder")}
              style={styles.offerTitleInput}
            />
            <div style={{ marginTop: "1.25rem" }}>
              <Field
                label={t("yourNameBusiness")}
                placeholder={t("yourNameOrCompany")}
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
              />
              <Field
                label={t("email")}
                placeholder="email@example.com"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
              />
              <Field
                label={t("Phone")}
                placeholder="+972 50 000 0000"
                value={senderPhone}
                onChange={(e) => setSenderPhone(e.target.value)}
              />
            </div>
          </div>

          <div style={styles.headerRight}>
            <div style={{ marginTop: "1rem" }}>
              <div style={{ ...styles.label, justifyContent: "flex-end" }}>
                {t("validUntil")}
              </div>
              <input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                style={{ ...styles.input, textAlign: "right" }}
              />
            </div>
          </div>
        </div>

        {/* Prepared For */}
        <div style={styles.section}>
          <div style={styles.sectionLabel}>{t("preparedFor")}</div>
          <div style={styles.grid2}>
            <Field
              label={t("clientNameLabel")}
              placeholder={t("clientNamePlaceholder")}
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
            />
            <Field
              label={t("clientEmailLabel")}
              placeholder="client@example.com"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
            />
            <Field
              label={t("addressLabel")}
              placeholder={t("streetAddress")}
              value={clientAddress}
              onChange={(e) => setClientAddress(e.target.value)}
            />
            <Field
              label={t("cityCountry")}
              placeholder={t("cityCountry")}
              value={clientCity}
              onChange={(e) => setClientCity(e.target.value)}
            />
          </div>
        </div>

        {/* Services */}
        <div style={styles.section}>
          <div style={styles.sectionLabel}>{t("services")}</div>
          <div id="invoice-table-wrapper" style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: "42%" }}>
                    {t("description")}
                  </th>
                  <th style={{ ...styles.th, width: "14%" }}>{t("qty")}</th>
                  <th style={{ ...styles.th, width: "18%" }}>{t("rate")}</th>
                  <th
                    style={{
                      ...styles.th,
                      width: "18%",
                      textAlign: "right",
                      background: "#efede8",
                    }}
                  >
                    {t("amount")}
                  </th>
                  <th
                    className="no-print"
                    style={{ ...styles.th, width: "8%" }}
                  ></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const amt = getAmount(row);
                  return (
                    <tr
                      key={row.id}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#faf9f7")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "")
                      }
                    >
                      <td style={styles.td}>
                        <input
                          style={styles.tableInput}
                          placeholder={t("serviceDescription")}
                          value={row.description}
                          onChange={(e) =>
                            updateRow(row.id, "description", e.target.value)
                          }
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          style={{ ...styles.tableInput, width: "50px" }}
                          type="number"
                          placeholder="1"
                          min="0"
                          value={row.qty}
                          onChange={(e) =>
                            updateRow(row.id, "qty", e.target.value)
                          }
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          style={{ ...styles.tableInput, width: "70px" }}
                          type="number"
                          placeholder="0.00"
                          min="0"
                          value={row.rate}
                          onChange={(e) =>
                            updateRow(row.id, "rate", e.target.value)
                          }
                        />
                      </td>
                      <td style={{ ...styles.td, ...styles.amountCell }}>
                        {amt > 0 ? (
                          fmt(amt)
                        ) : (
                          <span style={{ color: "#ccc", fontWeight: "400" }}>
                            —
                          </span>
                        )}
                      </td>
                      <td
                        className="no-print"
                        style={{ ...styles.td, textAlign: "center" }}
                      >
                        <button
                          style={styles.deleteRowBtn}
                          title="Remove row"
                          onClick={() => deleteRow(row.id)}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = "#e53935")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = "#ccc")
                          }
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button
            className="no-print"
            style={styles.addRowBtn}
            onClick={addRow}
          >
            {t("addLineItem")}
          </button>
        </div>

        {/* Notes + Totals */}
        <div style={styles.totalsNotesRow}>
          <div style={styles.notesBox}>
            <div style={styles.sectionLabel}>{t("note")}</div>
            <textarea
              style={styles.notes}
              placeholder={t("notesPlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <div style={styles.totalsBlock}>
            {/* Subtotal */}
            <div style={styles.totalsRow}>
              <span>{t("subTotal")}</span>
              <span>{fmt(subtotal)}</span>
            </div>
            {/* Include tax toggle — hidden in PDF */}
            <div
              className="no-print"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "6px 0",
              }}
            >
              <button
                style={
                  includeTax ? styles.toggleBtnActive : styles.toggleBtn
                }
                onClick={() => setIncludeTax((v) => !v)}
              >
                {t("includeTaxInTotal")}
              </button>
            </div>
            {/* Tax row — only rendered when toggled on */}
            {includeTax && (
              <div style={styles.totalsRow}>
                <span
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  {t("taxRate")}{" "}
                  <input
                    style={styles.taxInput}
                    type="number"
                    placeholder="0"
                    min="0"
                    max="100"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                  />
                  %
                </span>
                <span>{fmt(tax)}</span>
              </div>
            )}
            {/* Total */}
            <div style={styles.totalsTotal}>
              <span>{t("total")}</span>
              <span>{fmt(total)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footerRow}>
          <span
            style={{
              fontSize: "12px",
              color: "#aaa",
              fontFamily: "'Georgia', serif",
              letterSpacing: "0.03em",
            }}
          >
            {t("thankYou")}
          </span>
          <button
            className="no-print"
            style={styles.printBtn}
            onClick={handlePrint}
          >
            {t("printSaveAsPdf")}
          </button>
        </div>

        {/* Branding */}
        <div
          style={{
            marginTop: "2rem",
            paddingTop: "1rem",
            borderTop: "1px dashed #e8e5e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
        >
          <span
            style={{
              fontSize: "11px",
              color: "#bbb",
              fontFamily: "'Georgia', serif",
              letterSpacing: "0.04em",
            }}
          >
            {t("createdBy")}
          </span>
          <a
            href="https://neholesek.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: "11px",
              fontFamily: "'Georgia', serif",
              fontWeight: "600",
              letterSpacing: "0.06em",
              color: "#888",
              textDecoration: "none",
              borderBottom: "1px solid #ddd",
              paddingBottom: "1px",
            }}
          >
            NeholEsek
          </a>
          <span
            style={{
              fontSize: "11px",
              color: "#ccc",
              fontFamily: "'Georgia', serif",
            }}
          >
            ·
          </span>
          <a
            href="https://neholesek.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: "11px",
              fontFamily: "'Georgia', serif",
              color: "#bbb",
              textDecoration: "none",
              letterSpacing: "0.02em",
            }}
          >
            neholesek.com
          </a>
        </div>
      </div>
    </div>
  );
}
